import React, { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { MathBlock } from "../components/MathRender";
import { Trophy, Plus, ArrowLeft, Clock, Award } from "lucide-react";

function ContestDetail({ contestId, onBack }) {
  const { user } = useAuth();
  const [c, setC] = useState(null);
  const [entries, setEntries] = useState([]);
  const [entry, setEntry] = useState("");

  const load = useCallback(() => {
    api.get(`/arena/contests/${contestId}`).then((r) => setC(r.data)).catch(() => {});
    if (["adjudicator", "admin", "customer"].includes(user?.role))
      api.get(`/arena/contests/${contestId}/entries`).then((r) => setEntries(r.data)).catch(() => {});
  }, [contestId, user]);
  useEffect(() => { load(); }, [load]);

  if (!c) return <div className="py-16 text-center text-slate-500">Loading contest…</div>;

  const submitEntry = async () => {
    if (!entry.trim()) { toast.error("Entry cannot be empty"); return; }
    try { await api.post(`/arena/contests/${contestId}/enter`, { content_latex: entry }); toast.success("Entry submitted"); setEntry(""); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not enter"); }
  };
  const judge = async (eid, score, rank) => {
    try { await api.post(`/arena/entries/${eid}/judge`, { score: Number(score), rank: Number(rank) }); toast.success("Entry judged"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not judge"); }
  };

  return (
    <div data-testid="contest-detail">
      <button onClick={onBack} className="mb-6 inline-flex items-center gap-2 text-sm text-slate-500 hover:text-slate-900"><ArrowLeft className="h-4 w-4" /> All contests</button>
      <span className="inline-flex rounded-full border border-amber-200 bg-amber-50 px-3 py-1 text-xs font-semibold text-amber-700" data-testid="contest-label">Speculative contest</span>
      <h2 className="mt-3 font-display text-3xl text-slate-900">{c.title}</h2>
      {c.description_latex && <div className="mt-4"><MathBlock>{c.description_latex}</MathBlock></div>}

      <div className="mt-5 grid grid-cols-1 gap-4 sm:grid-cols-2">
        <div className="axiom-card p-4"><p className="eyebrow">Eligibility</p><p className="mt-1 text-sm text-slate-600">{c.eligibility}</p></div>
        <div className="axiom-card p-4"><p className="eyebrow">Judging</p><p className="mt-1 text-sm text-slate-600">{c.judging_rules}</p></div>
        <div className="axiom-card p-4"><p className="eyebrow">Tie rule</p><p className="mt-1 text-sm text-slate-600">{c.tie_rule}</p></div>
        <div className="axiom-card p-4"><p className="eyebrow">Prize</p><p className="mt-1 text-sm text-slate-600">{c.prize_rules}</p></div>
      </div>
      <div className="mt-3 flex items-center gap-4 text-xs text-slate-500">
        <span className="inline-flex items-center gap-1.5 text-emerald-700"><Award className="h-4 w-4" /> ${c.prize_amount?.toLocaleString()}</span>
        <span className="inline-flex items-center gap-1.5"><Clock className="h-4 w-4" /> Closes {c.deadline ? new Date(c.deadline).toLocaleDateString() : "—"}</span>
        <span>{c.entry_count} entries</span>
      </div>

      {user?.role === "contributor" && (
        <div className="axiom-card mt-6 p-5" data-testid="entry-composer">
          <p className="eyebrow mb-2">Your entry (LaTeX)</p>
          <textarea data-testid="entry-latex" rows={4} value={entry} onChange={(e) => setEntry(e.target.value)}
            className="w-full rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 font-mono text-sm text-[#B45309] outline-none focus:border-[#E61A27]" />
          {entry && <div className="mt-2"><MathBlock>{entry}</MathBlock></div>}
          <button data-testid="entry-submit" onClick={submitEntry} className="btn-crimson mt-3 !py-2 text-sm">Submit entry</button>
        </div>
      )}

      {c.leaderboard?.length > 0 && (
        <div className="mt-8">
          <p className="eyebrow mb-3">Leaderboard</p>
          <div className="space-y-2">
            {c.leaderboard.map((e) => (
              <div key={e.id} className="axiom-card flex items-center justify-between p-3" data-testid={`leader-${e.id}`}>
                <span className="text-sm text-slate-700">#{e.rank} · {e.user_name}</span>
                <span className="text-sm font-semibold text-emerald-700">{e.score} pts</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {["adjudicator", "admin", "customer"].includes(user?.role) && entries.length > 0 && (
        <div className="mt-8">
          <p className="eyebrow mb-3">Entries ({entries.length})</p>
          <div className="space-y-3">
            {entries.map((e) => (
              <div key={e.id} className="axiom-card p-4" data-testid={`entry-${e.id}`}>
                <p className="text-sm text-slate-700">{e.user_name}</p>
                <div className="mt-2"><MathBlock>{e.content_latex}</MathBlock></div>
                {["adjudicator", "admin"].includes(user?.role) && (
                  <JudgeRow onJudge={(score, rank) => judge(e.id, score, rank)} current={e} />
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function JudgeRow({ onJudge, current }) {
  const [score, setScore] = useState(current.score ?? 80);
  const [rank, setRank] = useState(current.rank ?? 1);
  return (
    <div className="mt-3 flex items-center gap-2">
      <input data-testid="judge-score" type="number" value={score} onChange={(e) => setScore(e.target.value)} placeholder="score"
        className="w-24 rounded-lg border border-slate-200 bg-slate-50 px-2 py-1.5 text-sm outline-none focus:border-[#E61A27]" />
      <input data-testid="judge-rank" type="number" value={rank} onChange={(e) => setRank(e.target.value)} placeholder="rank"
        className="w-20 rounded-lg border border-slate-200 bg-slate-50 px-2 py-1.5 text-sm outline-none focus:border-[#E61A27]" />
      <button data-testid="judge-submit" onClick={() => onJudge(score, rank)} className="btn-ghost !px-4 !py-1.5 text-sm">Judge</button>
    </div>
  );
}

export default function Arena() {
  const { user } = useAuth();
  const [contests, setContests] = useState([]);
  const [active, setActive] = useState(null);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({ title: "", description_latex: "", eligibility: "", judging_rules: "", tie_rule: "Highest score; earliest entry wins on exact ties.", prize_rules: "", prize_amount: 0 });

  const load = useCallback(() => { api.get("/arena/contests").then((r) => setContests(r.data)).catch(() => {}); }, []);
  useEffect(() => { load(); }, [load]);

  const create = async () => {
    if (!form.title || !form.eligibility || !form.judging_rules || !form.prize_rules) { toast.error("Fill eligibility, judging and prize rules"); return; }
    try { const { data } = await api.post("/arena/contests", { ...form, prize_amount: Number(form.prize_amount) }); toast.success("Contest published"); setCreating(false); load(); setActive(data.id); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not publish"); }
  };
  const inp = "w-full rounded-lg border border-slate-200 bg-slate-50 px-3 py-2.5 text-slate-900 outline-none focus:border-[#E61A27]";

  return (
    <div className="mx-auto max-w-5xl px-5 py-12 sm:px-8" data-testid="arena-page">
      {active ? (
        <ContestDetail contestId={active} onBack={() => { setActive(null); load(); }} />
      ) : (
        <>
          <div className="flex flex-wrap items-end justify-between gap-4">
            <div>
              <p className="eyebrow mb-2">Arena</p>
              <h1 className="font-display text-3xl tracking-tight text-slate-900 sm:text-4xl flex items-center gap-3"><Trophy className="h-8 w-8 text-[#E61A27]" /> Competitions</h1>
              <p className="mt-3 max-w-2xl text-slate-600">Clearly-labelled speculative contests with published eligibility, judging, tie, and prize rules.</p>
            </div>
            {user?.role === "customer" && <button data-testid="new-contest-btn" onClick={() => setCreating(!creating)} className="btn-crimson"><Plus className="h-4 w-4" /> New contest</button>}
          </div>

          {creating && (
            <div className="axiom-card mt-6 space-y-3 p-6" data-testid="new-contest-form">
              <input data-testid="contest-title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="Contest title" className={inp} />
              <textarea data-testid="contest-desc" rows={2} value={form.description_latex} onChange={(e) => setForm({ ...form, description_latex: e.target.value })} placeholder="Problem (LaTeX)" className={`${inp} font-mono text-[#B45309]`} />
              <textarea data-testid="contest-eligibility" rows={2} value={form.eligibility} onChange={(e) => setForm({ ...form, eligibility: e.target.value })} placeholder="Eligibility rules" className={inp} />
              <textarea data-testid="contest-judging" rows={2} value={form.judging_rules} onChange={(e) => setForm({ ...form, judging_rules: e.target.value })} placeholder="Judging rules" className={inp} />
              <input data-testid="contest-tie" value={form.tie_rule} onChange={(e) => setForm({ ...form, tie_rule: e.target.value })} placeholder="Tie rule" className={inp} />
              <textarea data-testid="contest-prize-rules" rows={2} value={form.prize_rules} onChange={(e) => setForm({ ...form, prize_rules: e.target.value })} placeholder="Prize rules (label clearly as speculative)" className={inp} />
              <input data-testid="contest-prize-amount" type="number" value={form.prize_amount} onChange={(e) => setForm({ ...form, prize_amount: e.target.value })} placeholder="Prize amount (USD)" className={inp} />
              <button data-testid="contest-create" onClick={create} className="btn-crimson !py-2 text-sm">Publish contest</button>
            </div>
          )}

          <div className="mt-8 grid grid-cols-1 gap-5 md:grid-cols-2">
            {contests.map((c) => (
              <button key={c.id} data-testid={`contest-${c.id}`} onClick={() => setActive(c.id)} className="axiom-card p-6 text-left">
                <span className="inline-flex rounded-full border border-amber-200 bg-amber-50 px-2.5 py-0.5 text-[11px] font-semibold text-amber-700">Speculative</span>
                <h3 className="mt-2 font-display text-xl text-slate-900">{c.title}</h3>
                <p className="mt-2 flex items-center gap-4 text-xs text-slate-500">
                  <span className="inline-flex items-center gap-1 text-emerald-700"><Award className="h-4 w-4" /> ${c.prize_amount?.toLocaleString()}</span>
                  <span className="inline-flex items-center gap-1"><Clock className="h-4 w-4" /> {c.deadline ? new Date(c.deadline).toLocaleDateString() : "—"}</span>
                  <span>{c.entry_count} entries</span>
                </p>
              </button>
            ))}
          </div>
          {contests.length === 0 && <p className="mt-8 text-slate-500" data-testid="arena-empty">No contests yet.</p>}
        </>
      )}
    </div>
  );
}
